import 'altcha'; // registers the <altcha-widget> custom element
import { useEffect, useRef } from 'react';

// Custom-element JSX typing
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'altcha-widget': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        // altcha v3: `challenge` takes challenge data OR a URL string to fetch a
        // challenge from. (The classic `challengeurl` attribute is gone in v3 —
        // setting it silently leaves the URL empty and the widget fetches the
        // current page, yielding "expected application/json, received text/html".)
        challenge?: string;
      };
    }
  }
}

export function AltchaWidget({
  challengeUrl,
  onToken,
  onError,
  onReset,
  style,
}: {
  challengeUrl: string;
  onToken: (token: string) => void;
  onError?: (msg?: string) => void;
  onReset?: () => void;
  style?: React.CSSProperties;
}) {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // ALTCHA emits `statechange` with { state, payload }. state ∈
    // 'unverified' | 'verifying' | 'verified' | 'error' | 'expired'.
    const onState = (e: Event) => {
      const detail = (e as CustomEvent<{ state: string; payload?: string }>).detail;
      if (detail.state === 'verified' && detail.payload) onToken(detail.payload);
      else if (detail.state === 'error') onError?.();
      else if (detail.state === 'unverified' || detail.state === 'expired') onReset?.();
    };
    el.addEventListener('statechange', onState);
    return () => el.removeEventListener('statechange', onState);
  }, [onToken, onError, onReset]);

  // Theme the widget with our design tokens so it matches the inputs and adapts
  // to dark mode (ALTCHA exposes --altcha-* CSS custom properties).
  return (
    <altcha-widget
      ref={ref as never}
      challenge={challengeUrl}
      style={
        {
          display: 'block',
          width: '100%',
          // ALTCHA caps its inner box at --altcha-max-width (default 320px), so a
          // host width:100% alone leaves the box narrower than the inputs above.
          // Set it to 100% so the widget fills the form width and lines up.
          '--altcha-max-width': '100%',
          '--altcha-border-radius': '10px',
          '--altcha-color-base': 'var(--surface)',
          '--altcha-color-border': 'var(--hairline-strong)',
          '--altcha-color-text': 'var(--text)',
          ...style,
        } as React.CSSProperties
      }
    />
  );
}
